import {Collapse, Nav} from "react-bootstrap";
import {NavLink} from "react-router-dom";
import {
  Activity,
  BarChart2,
  Calendar,
  DollarSign,
  File,
  Layers,
  Lock,
  TrendingDown,
  TrendingUp,
  User
} from "react-feather";

import {useUser} from "../contexts/UserProvider";
import useTranslation from "../hooks/translations";
import {useSidebarState} from "../contexts/SidebarStateProvider";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import ProtectedContent from "./ProtectedContent";

export default function Sidebar() {
  const {user} = useUser();
  const translations = useTranslation();
  const {sidebarState, setSidebarState} = useSidebarState();

  return (
    <nav className="sidenav shadow-right sidenav-light">
      <div className="sidenav-menu">
        <div className="nav accordion" id="accordionSidenav">
          <div className="sidenav-menu-heading">Core</div>
          <ProtectedContent allowedRoles={["Admin", "Moderator"]}>
            <Nav.Item>
              <Nav.Link as={NavLink} to="/">
                <div className="nav-link-icon">
                  <Activity/>
                </div>
                {translations.dashboard}
              </Nav.Link>
            </Nav.Item>
          </ProtectedContent>
          <Nav.Item>
            <Nav.Link as={NavLink} to="/incomes">
              <div className="nav-link-icon">
                <TrendingUp/>
              </div>
              {translations.incomes}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link as={NavLink} to="/costs">
              <div className="nav-link-icon">
                <TrendingDown/>
              </div>
              {translations.costs}
            </Nav.Link>
          </Nav.Item>
          <ProtectedContent allowedRoles={["Admin", "Moderator"]}>
            <Nav.Link
              onClick={() => setSidebarState({
                ...sidebarState,
                isReportsOpen: !sidebarState.isReportsOpen
              })}
              aria-controls="reports-collapse"
              aria-expanded={sidebarState.isReportsOpen}
              className={sidebarState.isReportsOpen ? "" : "collapsed"}
            >
              <div className="nav-link-icon">
                <Layers/>
              </div>
              {translations.reports}
              <div className="sidenav-collapse-arrow">
                <FontAwesomeIcon icon="fa-angle-down"/>
              </div>
            </Nav.Link>
            <Collapse in={sidebarState.isReportsOpen}>
              <Nav className="sidenav-menu-nested nav accordion">
                <Nav.Link as={NavLink} to="/reports/quarterly-report">
                  <div className="nav-link-icon">
                    <Calendar/>
                  </div>
                  {translations.quarterlyReport}
                </Nav.Link>
                <Nav.Link as={NavLink} to="/reports/comparative-report">
                  <div className="nav-link-icon">
                    <BarChart2/>
                  </div>
                  {translations.comparativeReport}
                </Nav.Link>
              </Nav>
            </Collapse>
          </ProtectedContent>
          <div className="sidenav-menu-heading">Settings</div>
          <ProtectedContent allowedRoles={["Admin"]}>
            <Nav.Item>
              <Nav.Link as={NavLink} to="/settings/currency">
                <div className="nav-link-icon">
                  <DollarSign/>
                </div>
                {translations.currency}
              </Nav.Link>
            </Nav.Item>
          </ProtectedContent>
          <ProtectedContent allowedRoles={["Admin"]}>
            <Nav.Link
              onClick={() => setSidebarState({
                ...sidebarState,
                isIncomeSettingsOpen: !sidebarState.isIncomeSettingsOpen
              })}
              aria-controls="income-settings-collapse"
              aria-expanded={sidebarState.isIncomeSettingsOpen}
              className={sidebarState.isIncomeSettingsOpen ? "" : "collapsed"}
            >
              <div className="nav-link-icon">
                <TrendingUp/>
              </div>
              {translations.incomes}
              <div className="sidenav-collapse-arrow">
                <FontAwesomeIcon icon="fa-angle-down"/>
              </div>
            </Nav.Link>
            <Collapse in={sidebarState.isIncomeSettingsOpen}>
              <Nav className="sidenav-menu-nested nav accordion">
                <Nav.Link as={NavLink} to="/settings/income-type">
                  {translations.incomeType}
                </Nav.Link>
                <Nav.Link as={NavLink} to="/settings/income-category">
                  {translations.incomeCategory}
                </Nav.Link>
              </Nav>
            </Collapse>
          </ProtectedContent>
          <ProtectedContent allowedRoles={["Admin"]}>
            <Nav.Link
              onClick={() => setSidebarState({
                ...sidebarState,
                isCostSettingsOpen: !sidebarState.isCostSettingsOpen
              })}
              aria-controls="cost-settings-collapse"
              aria-expanded={sidebarState.isCostSettingsOpen}
              className={sidebarState.isCostSettingsOpen ? "" : "collapsed"}
            >
              <div className="nav-link-icon">
                <TrendingDown/>
              </div>
              {translations.costs}
              <div className="sidenav-collapse-arrow">
                <FontAwesomeIcon icon="fa-angle-down"/>
              </div>
            </Nav.Link>
            <Collapse in={sidebarState.isCostSettingsOpen}>
              <Nav className="sidenav-menu-nested nav accordion">
                <Nav.Link as={NavLink} to="/settings/cost-type">
                  {translations.costType}
                </Nav.Link>
                <Nav.Link as={NavLink} to="/settings/cost-category">
                  {translations.costCategory}
                </Nav.Link>
              </Nav>
            </Collapse>
          </ProtectedContent>
          <Nav.Link
            onClick={() => setSidebarState({
              ...sidebarState,
              isAccountSettingsOpen: !sidebarState.isAccountSettingsOpen
            })}
            aria-controls="account-settings-collapse"
            aria-expanded={sidebarState.isAccountSettingsOpen}
            className={sidebarState.isAccountSettingsOpen ? "" : "collapsed"}
          >
            <div className="nav-link-icon">
              <User/>
            </div>
            {translations.account}
            <div className="sidenav-collapse-arrow">
              <FontAwesomeIcon icon="fa-angle-down"/>
            </div>
          </Nav.Link>
          <Collapse in={sidebarState.isAccountSettingsOpen}>
            <Nav className="sidenav-menu-nested nav accordion">
              <Nav.Link as={NavLink} to="/account-profile">
                <div className="nav-link-icon">
                  <File/>
                </div>
                {translations.accountDetails}
              </Nav.Link>
              <Nav.Link as={NavLink} to="/account-security">
                <div className="nav-link-icon">
                  <Lock/>
                </div>
                {translations.security}
              </Nav.Link>
            </Nav>
          </Collapse>
        </div>
      </div>

      <div className="sidenav-footer">
        <div className="sidenav-footer-content">
          <div className="sidenav-footer-subtitle">Logged in as:</div>
          <div
            className="sidenav-footer-title">{`${user.first_name} ${user.last_name}`}</div>
        </div>
      </div>
    </nav>
  );
}
